import { Component, OnInit, OnDestroy, Input, ViewChild, ElementRef } from '@angular/core';
import { Subject } from 'rxjs';
import { ProfileService, TeamsService, UserModel } from '../../lockvue-ng-sdk';
import { NbStepperComponent, NbTagInputAddEvent, NbTagInputDirective } from '@nebular/theme';
import { NotificationMessagesService } from '../../services/notification-messages.service';
import { takeUntil } from 'rxjs/operators';
import { TeamDetailsUIModel } from '../../models/TeamDetailsUIModel';

@Component({
  selector: 'lockvue-team-edit',
  templateUrl: './team-edit.component.html',
  styleUrls: ['./team-edit.component.scss'],
})
export class TeamEditComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();

  inProgress: boolean;
  memberToAdd: string;
  private newMembersAdded = 0;
  private next$ = new Subject<boolean>();
  profile: UserModel;

  currentTeamMembers: Set<string>;

  @Input() value: TeamDetailsUIModel;

  @ViewChild('stepper', { static: true }) stepperComponent: NbStepperComponent;
  @ViewChild(NbTagInputDirective, { read: ElementRef }) tagInput: ElementRef<HTMLInputElement>;

  constructor(
    private teamsService: TeamsService,
    private notificationsService: NotificationMessagesService,
    private userProfile: ProfileService,
  ) {}

  ngOnInit() {
    this.currentTeamMembers = new Set(this.value.Members.map(m => m.UserId));
    this.userProfile
      .profileMy()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(profile => (this.profile = profile));
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  editTeam() {
    this.inProgress = true;
    this.teamsService
      .teamsEdit({
        Id: this.value.Id,
        Name: this.value.Name,
        Description: this.value.Description,
      })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        result => {
          this.inProgress = false;
          this.notificationsService.showSuccess('Success', 'Team updated successfully.');
        },
        error => {
          this.inProgress = false;
          this.notificationsService.showError(
            'Error',
            'Team modification failed. Error: ' + (error ? error.message : 'Unknown'),
          );
          this.stepperComponent.reset();
        },
      );
  }

  addSelectedMember(member: NbTagInputAddEvent) {
    if (member.value === this.profile.UserId) {
      this.notificationsService.showError('User Error!', "You can't add yourself to the team");
      this.tagInput.nativeElement.value = '';
      return;
    }
    if (
      member.value &&
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(member.value)
    ) {
      if (this.currentTeamMembers.has(member.value)) {
        this.notificationsService.showError(
          'User Exists!',
          'The user you have entered already exist in the team.',
        );
        this.tagInput.nativeElement.value = '';
      } else {
        return this.addMemberToTeam(member);
      }
    } else {
      if (this.newMembersAdded > 0 && this.memberToAdd === null) {
        this.stepperComponent.next();
      } else {
        this.notificationsService.showError('Invalid Email', 'Please enter a valid email');
      }
    }
  }

  private addMemberToTeam(member: NbTagInputAddEvent) {
    this.inProgress = true;
    this.memberToAdd = null;
    this.teamsService
      .teamsAddMember({
        TeamId: this.value.Id,
        UserId: member.value,
        AutoGrantPermissions: !(this.value.Members.length === 0),
        AutoGrantPermissionsFromUserId:
          this.value.Members.length === 0 ? '' : this.value.Members[0].UserId,
      })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        result => {
          this.inProgress = false;
          // only if the function is called from next()
          if (member.input === null) {
            this.next$.next(true);
          }
          this.currentTeamMembers.add(member.value);
          this.tagInput.nativeElement.value = '';
          this.newMembersAdded++;
          this.notificationsService.showSuccess(
            'Success',
            'Member was successfully added to the team.',
          );
          const numPermissions = result ? '' + result.length : 'No';
          this.notificationsService.showSuccess(
            'Permission Automation',
            numPermissions + ' permissions were automatically granted.',
          );
        },
        error => {
          this.next$.next(false);
          this.inProgress = false;
          this.notificationsService.showError(
            'Error',
            'Error while adding member to the team: ' + (error ? error.message : 'Unknown'),
          );
        },
      );
  }

  next() {
    // if user clicks on next than it means he want's to go to next step.
    // So we only subscribe to the next$ here.
    this.next$.pipe(takeUntil(this.unsubscribe$)).subscribe(next => {
      if (next) {
        this.stepperComponent.next();
      }
    });
    // to handle any previous teams that have no members
    if (this.currentTeamMembers.size === 0) {
      this.notificationsService.showError('No Member!', 'Add atleast one member in the team!');
    } else {
      this.addSelectedMember({ input: null, value: this.memberToAdd });
    }
  }
}
